import {
    CommandResponse,
    PlacementPayload,
    RenamePayload,
    RevisionedCommandResponse,
    SchemaSection,
    StudyEntityType,
    toSchemaSection,
} from '@faro/study-designer-model';
import axios from 'axios';
import { createStudyEntityHookFactory } from '../createStudyEntityHookFactory';
import { StudyPathParameter } from '../parameter';

const factory = createStudyEntityHookFactory<SchemaSection>(StudyEntityType.SchemaSection);

export const useSchemaSections = factory.list(
    (
        { studyId, ...query }: StudyPathParameter & { revision?: number },
        signal?: AbortSignal
    ): Promise<SchemaSection[]> => {
        return axios
            .get(`/api/study-designer/studies/${studyId}/schema-section`, {
                signal,
                params: query,
            })
            .then(response => response.data?.map(toSchemaSection) ?? []);
    }
);

export const useSchemaSectionUpdate = factory.mutate(
    (entity: SchemaSection): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .put(`/api/study-designer/studies/${entity.studyId}/schema-section/${entity.id}`, entity)
            .then(response => response.data);
    },
    {
        optimisticUpdate: (schemaSection, optimisticData) => {
            return optimisticData.map(x => (x.id === schemaSection.id ? schemaSection : x));
        },
    }
);

export const useSchemaSectionDelete = factory.mutate(
    (entity: { id: string; studyId: string }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        return axios
            .delete(`/api/study-designer/studies/${entity.studyId}/schema-section/${entity.id}`)
            .then(response => response.data);
    }
);

export const useSchemaSectionMove = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        placement: PlacementPayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, placement } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/schema-section/${id}/actions/move`, placement)
            .then(response => response.data);
    }
);

export const useSchemaSectionLink = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        linkedSection: Partial<SchemaSection> & Pick<SchemaSection, 'id' | 'studyId' | 'entityType'>;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, linkedSection } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/schema-section/${id}/actions/link`, linkedSection)
            .then(response => response.data);
    }
);

export const useSchemaSectionRename = factory.mutate(
    (entity: {
        id: string;
        studyId: string;
        renamePayload: RenamePayload;
    }): Promise<RevisionedCommandResponse<CommandResponse>> => {
        const { id, studyId, renamePayload } = entity;
        return axios
            .put(`/api/study-designer/studies/${studyId}/schema-section/${id}/actions/rename`, renamePayload)
            .then(response => response.data);
    }
);
